/**
 * What the Orb needs to know about something playing.
 *
 * The Orb is the single persistent control (Screen System §1), so audio that
 * outlives a page belongs to it and not to a second bar. HOTTR.news shipped a
 * floating now-playing panel first and it parked itself over the Orb — the one
 * control the system promises is always reachable.
 *
 * So the transport lives in the fan, nearest the thumb; the Orb's own material
 * says something is running; and the resting Orb carries a faint mark that says
 * come inside. Three things, one rule that shaped all of them:
 *
 *   The kit owns no audio. No element, no store, no timer. The host plays; this
 *   describes what is playing and hands back the two gestures worth having in a
 *   menu you opened with your thumb — pause it, or go to it.
 *
 * Scrubbing is deliberately absent. A seek bar wants a wide, precise target and
 * the fan is a curved column of 44px rows over photography. The place to scrub
 * is the article's own player, which is what `onOpen` goes to.
 */

/** Mirrors a media element's readyState in the shapes a menu can act on. */
export type MediaStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

export interface OrbMedia {
  /** What is playing. Shown as a row; keep it short — it truncates. */
  title: string;
  status: MediaStatus;
  /** Play or pause, whichever the current status calls for. */
  onToggle: () => void;
  /** Go to whatever is playing. Omit and the title row is not rendered. */
  onOpen?: () => void;
}

/** True while the Orb should read as alive. Loading counts: the user pressed. */
export const mediaIsLive = (media?: OrbMedia | null): boolean =>
  media?.status === 'playing' || media?.status === 'loading';

/**
 * The transport row's label.
 *
 * `error` says RETRY rather than PLAY because pressing it does retry, and a
 * control that lies about what it will do is worse than one that admits the
 * failure.
 */
export const mediaToggleLabel = (media: OrbMedia): string => {
  switch (media.status) {
    case 'playing':
      return 'Pause';
    case 'loading':
      return 'Loading';
    case 'error':
      return 'Retry';
    default:
      return 'Play';
  }
};

/** Material Symbols name for the transport row. */
export const mediaToggleIcon = (media: OrbMedia): string => {
  switch (media.status) {
    case 'playing':
      return 'pause';
    case 'loading':
      return 'progress_activity';
    case 'error':
      return 'refresh';
    default:
      return 'play_arrow';
  }
};

/**
 * The mark the resting Orb holds while there is something in its menu.
 *
 * NOT a control, and not a label for the press. It says COME INSIDE — the fan
 * has something in it, and here is a hint what. Audio is only the first thing
 * to want it; an unread message wants `mail`, a queue wants a count. That is
 * why it is its own prop on OrbFan and OrbRail, and only DERIVED from media
 * when nothing else has asked for it.
 *
 * It sits inside the Orb, and fan-media.css is where the whole argument for
 * that lives — including why it does not break chassis.css's rule against a
 * glyph on the Orb. Short version: the rule exists to stop the Orb becoming a
 * toggle, the press behaviour never changes, and the mark is screen-blended so
 * it can only add light and never cover the face.
 */
export interface OrbIndicator {
  /** Material Symbols glyph. `pause` today, `mail` the day messages land. */
  icon: string;
  /** What the mark means. Announced; never drawn. */
  label: string;
}

/** The mark audio asks for, or null when nothing is loaded. */
export const mediaIndicator = (media?: OrbMedia | null): OrbIndicator | null => {
  if (!media) return null;
  return {
    icon: mediaToggleIcon(media),
    label:
      media.status === 'playing'
        ? `Playing: ${media.title}`
        : `Paused: ${media.title}`,
  };
};
